import { FormModal, type SubmitProps } from '@/components/form-modal.js';
import { Button } from '@/components/ui/button.js';
import { DialogDescription } from '@/components/ui/dialog.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { deleteAssessmentContent, getAssessmentContents, getAssessments } from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import type { AssessmentContent } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { z } from 'zod';

const deleteAssessmentContentSchema = z.object({});

export interface DeleteAssessmentContentProps {
  assessmentContent: AssessmentContent;
  trigger?: ReactNode;
  onDeleted?: () => void | Promise<unknown>;
}

export function DeleteAssessmentContent({ assessmentContent, trigger, onDeleted }: DeleteAssessmentContentProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({ mutationFn: deleteAssessmentContent });

  const onSubmit = async ({ closeModal }: SubmitProps<z.infer<typeof deleteAssessmentContentSchema>>) => {
    try {
      await mutateAsync(assessmentContent.id);
    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        description: airServerErrorFrom(error)?.userFacingMessage ?? 'Failed to delete assessment content.',
      });
      return;
    }

    toast({ description: 'Assessment content deleted successfully' });
    closeModal();
    await onDeleted?.();
    await Promise.all([
      queryClient.invalidateQueries(getAssessmentContents()),
      queryClient.invalidateQueries(getAssessments()),
    ]);
  };

  return (
    <FormModal
      modalTitle="Delete assessment content"
      dataTestId="assessment-content-modal-delete"
      trigger={
        trigger ?? (
          <Button
            variant="outline"
            size="sm"
            title="Delete"
            aria-label={`Delete ${assessmentContent.name}`}
            data-testid={`assessment-content-row-button-delete-${assessmentContent.id}`}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </Button>
        )
      }
      formSchema={deleteAssessmentContentSchema}
      onSubmit={onSubmit}
      confirmBtnLabel="Delete"
      submitButtonProps={{ variant: 'destructive' }}
      dialogContentProps={{ className: 'max-w-md' }}
    >
      {() => (
        <DialogDescription>
          Are you sure you want to delete “{assessmentContent.name}”? This action cannot be undone.
        </DialogDescription>
      )}
    </FormModal>
  );
}
